/* eslint-disable no-nested-ternary */
/* eslint-disable complexity */
import React from 'react';
import { Rooms } from '@search/filter-enums/enums/Rooms';
import { DealType } from '@search/filter-enums/enums';
import { useGqlContext } from '@search/gql-client/src/gqlContext';
import { useGql2Loader, Gql2CacheInfoCallback } from '@search/gql-client/src/useGql2';

import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { price2Compat } from '../../../../offer/search/page/SearchOffersQuery';
import { areaCompat } from '../../../../offer/search/page/SearchOffersQuery/helpers/areaCompat';
import { roomTypeFilterCompat } from '../../../../offer/search/page/SearchOffersQuery/helpers/roomTypeFilterCompat';

import { OfferYMapApiPlacemarkResponseItem } from '../../api/placemarkResponse';
import {
    SearchOffersMapQuery$data as SearchOffersMapQueryResponse, SearchOffersMapQuery$variables as SearchOffersMapQueryVariables
} from './__generated__/SearchOffersMapQuery.graphql';
import { SearchOffersMapQuery } from './SearchOffersMapQuery';

const cacheInfo: Gql2CacheInfoCallback<
    'skipGeo' | 'skipNb',
    SearchOffersMapQueryVariables,
    SearchOffersMapQueryResponse
> = v => ({
    skipGeo: {
        responseKey: 'geo',
        cacheKey: String(
            v.regionId === RegionIdEnum.MSK_OBL ?
                RegionIdEnum.MSK_AND_MSK_OBL :
                (
                    v.regionId === RegionIdEnum.LEN_OBL ?
                        RegionIdEnum.SPB_AND_LEN_OBL :
                        v.regionId
                )
        ) + 'id-' + String(v.geoIds?.join('.') ?? '')
    },
    skipNb: {
        responseKey: 'newBuilding',
        cacheKey: v.filters?.newBuildingIds?.length ? v.filters.newBuildingIds.join('.') : undefined
    }
});

function useSearchOffersMapFetcher() {
    const gql = useGqlContext();

    return React.useCallback(
        (vars: SearchOffersMapQueryVariables, opts?: { signal?: AbortSignal }) => gql.client<
            SearchOffersMapQueryVariables,
            SearchOffersMapQueryResponse
        >(SearchOffersMapQuery, vars, opts).then(),
        [ gql ]
    );
}

export function useSearchOffersMapLoader() {
    return useGql2Loader(useSearchOffersMapFetcher(), {
        cachePrefix: 'SearchOffersMap',
        cacheInfo,
        authTrack: true
    });
}

function getOfferBase(
    gqlPoint: Extract<
        SearchOffersMapQueryResponse['searchOffersMap']['points'][number],
        {
            __typename: 'SingleOfferPointFlat' | 'SingleOfferPointRoom'
                | 'SingleOfferPointHouse' | 'SingleOfferPointLand';
        }
    >
) {
    const offer = gqlPoint.offer;

    if (! offer.location.coordinates) throw new Error(`Unknown coordinates type: offer ${offer.id}`);

    const priceRaw = offer.dealType?.__typename === 'ProfOfferDealRent' || offer.dealType?.__typename === 'ProfOfferDealSell' ?
        offer.dealType.price ?? undefined :
        undefined;

    const price = price2Compat(priceRaw!, 1)!;

    return {
        id: offer.id,
        coordinates: offer.location.coordinates,
        price,
        rent: offer.dealType?.__typename === 'ProfOfferDealRent' ? {
            untilitesIncluded: offer.dealType.utilities?.has ?? false
        } : undefined,
        preview: {
            fullPath: offer.gallery?.mainImage.preview1 ?? ''
        },
        regionId: offer.location.narrowRegion?.id ?? 3,
        url: '',
        dealType: offer.dealType?.__typename === 'ProfOfferDealRent' ? DealType.RENT : DealType.SELL,
        hasAnyPriority: undefined,
        isHighlighted: offer.status?.hasHighlight,
        isFavorite: offer.isFavorite ?? undefined
    };
}

// eslint-disable-next-line complexity
export function pointCompat(
    gqlPoint: SearchOffersMapQueryResponse['searchOffersMap']['points'][number]
): OfferYMapApiPlacemarkResponseItem {
    switch (gqlPoint?.__typename) {
        case 'TaglessOfferPoint': {
            return {
                kind: 'tagless',
                coordinates: gqlPoint.coordinates,
                geohash: gqlPoint.geoHash
            };
        }

        case 'SingleOfferPointFlat': {
            const ro = gqlPoint.offer.realtyObject?.__typename === 'ProfOfferFlat' ?
                gqlPoint.offer.realtyObject : undefined;

            return {
                ...getOfferBase(gqlPoint),
                kind: 'flat',
                dataTest: 'singlePin',
                isVisited: gqlPoint.offer?.stats?.isViewed ?? false,
                floor: ro?.characteristics?.floor ?? 1,
                floorsTotal: ro?.building?.floorsTotal ?? 1,
                isNewBuilding: ro?.building?.isNewbuilding ?? false,
                totalArea: areaCompat(ro?.totalArea),
                roomType: (ro?.roomType ? roomTypeFilterCompat(ro?.roomType) : undefined) ?? Rooms.UNKNOWN,
            };
        }

        case 'SingleOfferPointRoom': {
            const ro = gqlPoint.offer.realtyObject?.__typename === 'ProfOfferRoom' ?
                gqlPoint.offer.realtyObject : undefined;

            return {
                ...getOfferBase(gqlPoint),
                kind: 'room',
                dataTest: 'singlePin',
                isVisited: gqlPoint.offer?.stats?.isViewed ?? false,
                floor: ro?.enclosingFlat?.floor ?? 1,
                floorsTotal: ro?.enclosingFlat?.building?.floorsTotal ?? 1,
                isNewBuilding: ro?.enclosingFlat?.building?.isNewbuilding ?? false,
                totalArea: ro?.livingArea ? areaCompat(ro.livingArea) : undefined,
            };
        }

        case 'SingleOfferPointHouse': {
            const ro = gqlPoint.offer.realtyObject?.__typename === 'ProfOfferHouse' ?
                gqlPoint.offer.realtyObject : undefined;

            return {
                ...getOfferBase(gqlPoint),
                kind: 'house',
                dataTest: 'singlePin',
                isVisited: gqlPoint.offer?.stats?.isViewed ?? false,
                totalArea: ro?.totalArea ? areaCompat(ro.totalArea) : undefined,
                landArea: ro?.land?.area ? areaCompat(ro.land.area) : undefined,
            };
        }

        case 'SingleOfferPointLand': {
            const ro = gqlPoint.offer.realtyObject?.__typename === 'ProfOfferLand' ?
                gqlPoint.offer.realtyObject : undefined;

            return {
                ...getOfferBase(gqlPoint),
                kind: 'land',
                dataTest: 'singlePin',
                isVisited: gqlPoint.offer?.stats?.isViewed ?? false,
                totalArea: areaCompat(ro?.area),
            };
        }

        case 'MultiOfferPointFlat':
        case 'MultiOfferPointRoom':
        case 'MultiOfferPointHouse':
        case 'MultiOfferPointLand':
            return {
                kind: typesCompat[gqlPoint.__typename],
                dataTest: 'multiPin',
                id: gqlPoint.id,
                addressId: gqlPoint.addressId,
                coordinates: gqlPoint.coordinates,
                geohash: gqlPoint.geoHash,
                count: gqlPoint.count,
                minPrice: {
                    ...price2Compat(gqlPoint.minPrice, 1),
                    rubPrice: gqlPoint.minPriceRub ?? undefined
                },
                minArea: areaCompat(gqlPoint.minArea),
                maxArea: gqlPoint.maxArea ? areaCompat(gqlPoint.maxArea) : undefined,
                url: '',
                isNewBuilding: gqlPoint.isNewBuilding ?? undefined,
                isHighlighted: gqlPoint.hasHighlight
            };
        default: throw new Error(`Unknown point: ${gqlPoint?.__typename}`);
    }
}

const typesCompat = {
    MultiOfferPointFlat: 'flatMulti',
    MultiOfferPointRoom: 'roomMulti',
    MultiOfferPointHouse: 'houseMulti',
    MultiOfferPointLand: 'landMulti'
    // 'SingleOfferPointFlat': 'flat',
    // 'SingleOfferPointRoom': 'room',
    // 'SingleOfferPointHouse': 'house',
    // 'SingleOfferPointLand': 'land'
} as const;
